/**
 * UnlockWallet — Password entry to unlock an existing wallet.
 *
 * Features: wrong password feedback, attempt counter, reset option.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { useWallet } from '../WalletContext.js';

const MAX_ATTEMPTS = 10;

export function UnlockWallet({ onReset }: { onReset: () => void }): ReactNode {
  const { actions } = useWallet();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const handleUnlock = useCallback(async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    if (attempts >= MAX_ATTEMPTS) {
      setError('Too many failed attempts. Reset your wallet to continue.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await actions.unlock(password);
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        setError('Too many failed attempts. Reset your wallet to continue.');
      } else {
        const remaining = MAX_ATTEMPTS - newAttempts;
        setError(`Wrong password. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
      }
      setPassword('');
    } finally {
      setLoading(false);
    }
  }, [password, attempts, actions]);

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Unlock Wallet</h2>
      </div>
      <div className="screen-body centered">
        <div className="lock-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <p className="text-secondary">Enter your password to access your wallet</p>

        <div className="form-group">
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && void handleUnlock()}
            disabled={attempts >= MAX_ATTEMPTS}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          className="btn-primary btn-full"
          onClick={() => void handleUnlock()}
          disabled={loading || !password || attempts >= MAX_ATTEMPTS}
        >
          {loading ? 'Unlocking...' : 'Unlock'}
        </button>

        <div className="reset-section">
          <p className="text-muted text-sm">
            Forgot your password? You can reset and re-import using your recovery phrase.
          </p>
          <button className="btn-ghost btn-sm" onClick={onReset}>
            Reset Wallet
          </button>
        </div>
      </div>
    </div>
  );
}
